import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tabs,
  Tab,
  Autocomplete,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import CountrySelector from "../../widgets/CountrySelector";
import StateSelector from "../../widgets/StateSelector";
import CitySelector from "../../widgets/CitySelector";
import { MuiTelInput } from "mui-tel-input";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { getErrorResponse, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getOnsiteHeader, isOnsiteActivated } from "./utils";
import { LoadingButton } from "@mui/lab";
import { printRow } from "./ImtexBadgeDimension";
import useCommonGetInit from "../../hooks/useCommonGetInit";
import MuPb from "../../widgets/MuPb";
import inviteeBg from "../../assets/special_invitee.jpg";
import QRCode from "react-qr-code";

export const SPOT_REG_CATEGORIES = [
  "EXHIBITOR",
  "DELEGATION",
  "VIP",
  "EC",
  "SERVICE",
  "MEDIA",
  "STUDENT",
  "VISITOR",
];

// Badge Preview Component
const BadgePreview = ({
  name,
  companyName,
  barcodeId,
  backgroundImage,
  open,
  onClose,
  onPrint,
}) => {
  const badgeRef = useRef(null);

  // Convert cm to pixels (assuming 96 DPI)
  const cmToPixels = (cm) => (cm * 96) / 2.54;

  const badgeWidth = cmToPixels(10.52);
  const badgeHeight = cmToPixels(14.88);
  const topMargin = cmToPixels(6);
  const qrSize = 80; // Standard QR code size in pixels

  const handlePrint = () => {
    if (badgeRef.current) {
      // Convert SVG QR code to canvas and then to data URL
      const qrSvg = badgeRef.current.querySelector("svg");
      let qrDataUrl = "";

      if (qrSvg && barcodeId) {
        const canvas = document.createElement("canvas");
        canvas.width = qrSize;
        canvas.height = qrSize;
        const ctx = canvas.getContext("2d");

        // Create a data URL from the SVG
        const svgData = new XMLSerializer().serializeToString(qrSvg);
        const svgBlob = new Blob([svgData], {
          type: "image/svg+xml;charset=utf-8",
        });
        const svgUrl = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, qrSize, qrSize);
          ctx.drawImage(img, 0, 0);
          qrDataUrl = canvas.toDataURL("image/png");
          URL.revokeObjectURL(svgUrl);

          // Proceed with printing after QR code is converted
          printBadge(qrDataUrl);
        };
        img.src = svgUrl;
      } else {
        printBadge("");
      }
    }

    if (onPrint) onPrint();
  };

  const printBadge = (qrDataUrl) => {
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Badge</title>
          <style>
  /* Load Roboto */
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap');

  @page {
    size: ${badgeWidth}px ${badgeHeight}px;
    margin: 0;
  }

  /* Global font */
  body {
    margin: 0;
    padding: 0;
    font-family: 'Roboto', Arial, sans-serif;
  }

  .badge-container {
    width: ${badgeWidth}px;
    height: ${badgeHeight}px;
    position: relative;
    overflow: hidden;
    font-family: 'Roboto', Arial, sans-serif;
  }

  .badge-background {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  .badge-content {
    position: absolute;
    top: ${topMargin}px;
    left: 0;
    right: 0;
    text-align: center;
    padding: 0 20px;
    color: #000;
    font-family: 'Roboto', Arial, sans-serif;
  }

  .badge-name {
    font-size: 24px;
    font-weight: 700; /* Roboto Bold */
    margin-bottom: 12px;
    text-transform: uppercase;
    text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
  }

  .badge-company {
    font-size: 18px;
    font-weight: 500; /* Roboto Medium */
    margin-bottom: 16px;
    text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
  }

  .qr-container {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }

  .qr-box {
    background: white;
    padding: 8px;
    border-radius: 4px;
    display: inline-block;
  }

  .qr-image {
    display: block;
    width: ${qrSize}px;
    height: ${qrSize}px;
  }
</style>

        </head>
        <body>
          <div class="badge-container">
            <img src="${backgroundImage}" alt="Badge Background" class="badge-background" />
            <div class="badge-content">
              <div class="badge-name">${name}</div>
              <div class="badge-company">${companyName}</div>
              ${
                qrDataUrl
                  ? `
                <div class="qr-container">
                  <div class="qr-box">
                    <img src="${qrDataUrl}" alt="QR Code" class="qr-image" />
                  </div>
                </div>
              `
                  : ""
              }
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent>
        <Box
          ref={badgeRef}
          sx={{
            width: `${badgeWidth}px`,
            height: `${badgeHeight}px`,
            position: "relative",
            margin: "0 auto",
            overflow: "hidden",
            boxShadow: 3,
            border: "1px solid #ddd",
          }}
        >
          <img
            src={backgroundImage}
            alt="Badge Background"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: `${topMargin}px`,
              left: 0,
              right: 0,
              textAlign: "center",
              padding: "0 20px",
              color: "#000",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                marginBottom: "12px",
                textTransform: "uppercase",
                textShadow: "1px 1px 2px rgba(255,255,255,0.8)",
              }}
            >
              {name}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                marginBottom: "16px",
                textShadow: "1px 1px 2px rgba(255,255,255,0.8)",
              }}
            >
              {companyName}
            </Typography>
            {barcodeId && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "16px",
                }}
              >
                <Box
                  sx={{
                    background: "white",
                    padding: "8px",
                    borderRadius: "4px",
                    display: "inline-block",
                  }}
                >
                  <QRCode value={barcodeId} size={qrSize} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
        <Button onClick={handlePrint} variant="contained" color="primary">
          Print Badge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SpotRegistration = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: Mdata,
    isLoading,
    isError,
    error,
  } = useCommonGetInit(`${API_ENDPOINT}user/check-onsite?id=${id}`);

  const [registration, setRegistration] = useState({
    title: "",
    firstName: "",
    lastName: "",
    designation: "",
    mobileNo: "",
    email: "",
    companyName: "",
    address: "",
    country: null,
    state: null,
    city: null,
    pinZip: "",
    website: "",
    telephoneNo: "",
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [badgeData, setBadgeData] = useState(null);
  const [showBadge, setShowBadge] = useState(false);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (Mdata) {
      // if (!isOnsiteActivated(Mdata.module)) {
      //   navigate(-1);
      // }
    }
  }, [Mdata]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_ENDPOINT}user/spot-register?type=${SPOT_REG_CATEGORIES[selectedTab]}`,
        registration,
        getOnsiteHeader(Mdata.module)
      );
      toast.success(data.message);

      // // Store badge data and show badge preview
      // setBadgeData({
      //   name: `${registration.firstName} ${registration.lastName}`,
      //   companyName: registration.companyName,
      //   barcodeId: data.user?.barcode_id || data.user?.id || null,
      // });
      // setShowBadge(true);

      setTimeout(() => {
        // Optional: Call existing print function if needed
        printRow(data.user);

        // Reset form
        setRegistration({
          title: "",
          firstName: "",
          lastName: "",
          designation: "",
          mobileNo: "",
          email: "",
          companyName: "",
          address: "",
          country: null,
          state: null,
          city: null,
          pinZip: "",
          website: "",
          telephoneNo: "",
        });
      }, 500);
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseBadge = () => {
    setShowBadge(false);
    setBadgeData(null);
  };

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{error?.message}</Alert>;
  }

  return (
    <Box>
      <Box mb={2}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="Registration Categories Tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {SPOT_REG_CATEGORIES.map((category, index) => (
            <Tab key={index} label={category} />
          ))}
        </Tabs>
      </Box>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2}>
            <FormControl required size="small" fullWidth>
              <InputLabel>Title</InputLabel>
              <Select
                value={registration.title}
                label="Title"
                onChange={(e) =>
                  setRegistration({ ...registration, title: e.target.value })
                }
              >
                {["Mr.", "Mrs.", "Ms.", "Miss.", "Dr.", "Prof."].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              size="small"
              required
              label="First Name"
              value={registration.firstName}
              onChange={(e) =>
                setRegistration({
                  ...registration,
                  firstName: e.target.value,
                })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              required
              size="small"
              label="Last Name"
              value={registration.lastName}
              onChange={(e) =>
                setRegistration({ ...registration, lastName: e.target.value })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={[
                "Chairman",
                "Managing Director",
                "CEO",
                "President",
                "Country Head",
                "Executive Director",
                "Vice President",
                "Sr. Vice President",
                "CTO",
                "COO",
                "CFO",
                "General Manager",
                "Sr. Director",
                "Director",
                "Deputy Director",
                "General Manager",
                "Manager",
                "Production Head",
                "Technician",
                "Engineer",
                "Machine Operator",
                "Production/ Plant/ Technical Head",
                "Trainees",
                "Consultant",
                "Executive",
                "Partner",
                "Advisor",
                "Programmer",
                "Contractor",
                "Engineer",
              ]}
              value={registration.designation}
              onChange={(event, newValue) =>
                setRegistration({ ...registration, designation: newValue })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Designation"
                  required
                  size="small"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              required
              label={selectedTab === 6 ? "Institute Name" : "Company Name"}
              value={registration.companyName}
              onChange={(e) =>
                setRegistration({
                  ...registration,
                  companyName: e.target.value,
                })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small"
              label="Address"
              value={registration.address}
              onChange={(e) =>
                setRegistration({ ...registration, address: e.target.value })
              }
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CountrySelector
              selectedCountryCode=""
              selectedCountry={registration.country || null}
              onChange={(newV) =>
                setRegistration({ ...registration, country: newV })
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StateSelector
              countryCode={registration.country?.code}
              selectedState={registration.state || null}
              onChange={(newV) =>
                setRegistration({ ...registration, state: newV })
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CitySelector
              selectedCity={registration.city || null}
              onChange={(newV) =>
                setRegistration({ ...registration, city: newV })
              }
              countryCode={registration.country?.code || null}
              stateCode={registration.state?.code || null}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              label="PIN/ZIP"
              value={registration.pinZip}
              onChange={(e) =>
                setRegistration({ ...registration, pinZip: e.target.value })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              size="small"
              label="Website"
              value={registration.website}
              onChange={(e) =>
                setRegistration({ ...registration, website: e.target.value })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              required
              size="small"
              label="Email"
              type="email"
              value={registration.email}
              onChange={(e) =>
                setRegistration({ ...registration, email: e.target.value })
              }
              fullWidth
            />
          </Grid>
          <Grid item sm={4}>
            <MuiTelInput
              fullWidth
              size="small"
              label="Mobile No"
              defaultCountry="IN"
              forceCallingCode
              value={registration.mobileNo}
              onChange={(newValue) =>
                setRegistration({ ...registration, mobileNo: newValue })
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              label="Telephone No"
              value={registration.telephoneNo}
              onChange={(e) =>
                setRegistration({
                  ...registration,
                  telephoneNo: e.target.value,
                })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              loading={submitting}
              style={{ marginTop: "20px" }}
            >
              Submit
            </LoadingButton>
          </Grid>
        </Grid>
      </form>

      {/* Badge Preview Dialog */}
      {badgeData && (
        <BadgePreview
          name={badgeData.name}
          companyName={badgeData.companyName}
          barcodeId={badgeData.barcodeId}
          backgroundImage={inviteeBg}
          open={showBadge}
          onClose={handleCloseBadge}
          onPrint={() => {
            // Optional: Call existing print function
            // printRow(data.user);
          }}
        />
      )}
    </Box>
  );
};

export default SpotRegistration;
